import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Copy, ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/pages/interface/components/ui/dialog";
import { Button } from "@/pages/interface/components/ui/button";
import { Skeleton } from "@/pages/interface/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { getSyncDiff, SyncDiff } from "@/api/syncApi";
import type { VendorConfig } from "./vendorConfig";
import { diffRendered, detectFormat, toRenderableHtml, ContentFormat, MARK_THEIRS_CLASS, MARK_OURS_CLASS } from "./contentDiff";

/**
 * Stacked conflict review (design handoff `design_handoff_conflict_review_1a`,
 * option 1A). Replaces the old two-narrow-columns raw-source DiffReviewModal:
 * a wide modal, your version on top and theirs below, both rendered in the
 * same type stack, with word-level changes highlighted on the incoming side.
 *
 * `onAccept` / `onKeepOurs` must resolve `true` on success — the modal owns
 * the Applying / Apply-failed states (§8.5–8.6) and only closes itself once a
 * resolution actually lands, so a failed resolve can never look like a silent
 * no-op.
 */
export interface ConflictReviewModalProps {
    storyId: string;
    /** Story title for the header; falls back to the external key. */
    storyTitle?: string | null;
    mode: "remote" | "conflict";
    vendor: VendorConfig;
    onClose: () => void;
    onAccept: () => Promise<boolean>;
    onKeepOurs: () => Promise<boolean>;
}

const FIELD_DEFS: Array<{ key: string; label: string; rich?: boolean }> = [
    { key: "description_html", label: "Description", rich: true },
    { key: "title", label: "Title" },
    { key: "priority", label: "Priority" },
    { key: "story_points", label: "Story points" },
    { key: "labels", label: "Labels" },
    { key: "status", label: "Status" },
];

const MONO = "font-mono text-[12.5px]";

function displayValue(v: unknown): string {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
}

function valuesEqual(a: unknown, b: unknown): boolean {
    const normalize = (v: unknown) => (v === undefined || v === null || v === "" ? null : v);
    const na = normalize(a);
    const nb = normalize(b);
    if (na === nb) return true;
    if (typeof na === "object" || typeof nb === "object") {
        return JSON.stringify(na) === JSON.stringify(nb);
    }
    return false;
}

/** Shared type stack for both panes (§5) — equivalence makes comparison possible. */
const RENDER_STACK = cn(
    "text-sm leading-[1.7] text-slate-600 [text-wrap:pretty]",
    "[&_h1]:text-[17px] [&_h1]:font-semibold [&_h1]:text-slate-900 [&_h1]:tracking-tight [&_h1]:mb-1",
    "[&_h2]:text-[17px] [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:tracking-tight [&_h2]:mb-1 [&_h2]:mt-3",
    "[&_h3]:text-[10px] [&_h3]:font-extrabold [&_h3]:uppercase [&_h3]:tracking-[0.1em] [&_h3]:text-slate-400 [&_h3]:mt-3",
    "[&_h4]:text-[10px] [&_h4]:font-extrabold [&_h4]:uppercase [&_h4]:tracking-[0.1em] [&_h4]:text-slate-400 [&_h4]:mt-3",
    "[&_p]:mb-2 [&_strong]:font-semibold [&_strong]:text-slate-800",
    "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-1.5",
    "[&_code]:font-mono [&_code]:text-[12.5px] [&_pre]:font-mono [&_pre]:text-[12.5px] [&_pre]:whitespace-pre-wrap",
    "[&_table]:border [&_table]:border-slate-200 [&_th]:bg-slate-50 [&_th]:border [&_th]:border-slate-200 [&_th]:px-2 [&_th]:py-1 [&_th]:text-[13px] [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_td]:text-[13px]",
    "[&_a]:text-indigo-600 hover:[&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-slate-200 [&_blockquote]:pl-3",
    // §6 highlight treatments
    `[&_.${MARK_THEIRS_CLASS}]:bg-[#fde68a] [&_.${MARK_THEIRS_CLASS}]:text-[#78350f] [&_.${MARK_THEIRS_CLASS}]:px-1 [&_.${MARK_THEIRS_CLASS}]:rounded`,
    `[&_.${MARK_OURS_CLASS}]:bg-[#fde68a] [&_.${MARK_OURS_CLASS}]:text-[#78350f] [&_.${MARK_OURS_CLASS}]:px-1 [&_.${MARK_OURS_CLASS}]:rounded [&_.${MARK_OURS_CLASS}]:line-through [&_.${MARK_OURS_CLASS}]:opacity-75`,
);

const FORMAT_LABELS: Record<ContentFormat, string> = {
    html: "HTML",
    markdown: "Markdown",
    plaintext: "Text",
};

interface PaneProps {
    side: "ours" | "theirs";
    vendor: VendorConfig;
    html?: string;         // rendered (sanitized, possibly marked) HTML
    text?: string;         // plaintext / scalar rendering
    raw: string;           // source view + Copy payload
    format: ContentFormat; // detected source format, shown on the chip
    view: "rendered" | "source";
    renderFailed?: boolean;
    externalKey?: string | null;
    externalUrl?: string | null;
    editedNote: string;
}

function Pane({ side, vendor, html, text, raw, format, view, renderFailed, externalKey, externalUrl, editedNote }: PaneProps) {
    const ours = side === "ours";
    const showSource = view === "source" || renderFailed;
    return (
        <section
            role="region"
            aria-label={ours ? "Your version" : `${vendor.label} version`}
            className="flex flex-col gap-1.5"
        >
            <div className="flex items-center gap-2">
                <span className={cn(
                    "text-[10px] font-extrabold uppercase tracking-[0.1em]",
                    ours ? "text-indigo-700" : "text-[#92400e]",
                )}>
                    {ours ? "Your version" : `${vendor.label} version`}
                </span>
                <span className={cn(MONO, "rounded-[5px] border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-slate-500")}>
                    {FORMAT_LABELS[format]}
                </span>
                <span className="text-[11.5px] text-slate-400">{editedNote}</span>
                <span className="ml-auto">
                    {ours ? (
                        <button
                            type="button"
                            onClick={() => { void navigator.clipboard?.writeText(raw); }}
                            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        >
                            <Copy size={13} /> Copy
                        </button>
                    ) : externalUrl ? (
                        <a
                            href={externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        >
                            <ExternalLink size={13} /> {`Open ${externalKey ?? vendor.label}`}
                        </a>
                    ) : null}
                </span>
            </div>
            {renderFailed && (
                <p className="text-xs text-amber-600">Could not render this version. Showing source.</p>
            )}
            <div
                tabIndex={0}
                className={cn(
                    "h-[236px] overflow-y-auto rounded-[11px] px-6 py-5",
                    ours
                        ? "border-[1.5px] border-indigo-200 bg-white ring-[3px] ring-indigo-50"
                        : "border border-[rgba(217,119,6,0.32)] bg-[rgba(217,119,6,0.035)]",
                    showSource && "bg-slate-50",
                )}
            >
                {showSource ? (
                    <pre className={cn(MONO, "whitespace-pre-wrap break-words leading-[1.65] text-slate-600")}>{raw}</pre>
                ) : text !== undefined ? (
                    <div className={cn(RENDER_STACK, "whitespace-pre-wrap")}>{text}</div>
                ) : (
                    // Sanitized upstream in contentDiff (DOMPurify) — never raw input.
                    <div className={RENDER_STACK} dangerouslySetInnerHTML={{ __html: html ?? "" }} />
                )}
            </div>
        </section>
    );
}

export function ConflictReviewModal({ storyId, storyTitle, mode, vendor, onClose, onAccept, onKeepOurs }: ConflictReviewModalProps) {
    const { getToken } = useAuth();
    const [diff, setDiff] = useState<SyncDiff | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeField, setActiveField] = useState<string | null>(null);
    const [view, setView] = useState<"rendered" | "source">("rendered");
    const [applying, setApplying] = useState<"ours" | "theirs" | null>(null);
    const [applyFailed, setApplyFailed] = useState<"ours" | "theirs" | null>(null);
    const activeTabRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const token = await getToken();
                const result = await getSyncDiff(storyId, token ?? "");
                if (!cancelled) setDiff(result);
            } catch (e) {
                if (!cancelled) setLoadError(e instanceof Error ? e.message : "Failed to load changes");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [storyId, getToken]);

    const fields = useMemo(() => FIELD_DEFS
        .filter(f => diff?.fields?.[f.key] !== undefined)
        .map(f => ({ ...f, differs: diff ? !valuesEqual(diff.fields[f.key].local, diff.fields[f.key].remote) : false })),
        [diff]);
    const differingCount = fields.filter(f => f.differs).length;

    // Default active tab = first differing field, not the first field (§3).
    const currentKey = activeField ?? fields.find(f => f.differs)?.key ?? fields[0]?.key ?? null;
    const currentField = fields.find(f => f.key === currentKey) ?? null;
    const fieldDiff = currentKey && diff ? diff.fields[currentKey] : null;

    // Initial focus on the active field tab (§11).
    useEffect(() => {
        if (!loading && diff) activeTabRef.current?.focus();
    }, [loading, diff]);

    const rendered = useMemo(() => {
        if (!fieldDiff || !currentField) return null;
        const localRaw = displayValue(fieldDiff.local);
        const remoteRaw = displayValue(fieldDiff.remote);
        if (!currentField.rich) {
            return { kind: "text" as const, oursText: localRaw, theirsText: remoteRaw,
                     oursFormat: "plaintext" as const, theirsFormat: "plaintext" as const };
        }
        // Formats are detected per side — a Markdown work product regularly
        // reviews against an HTML tracker item. Each side converts to HTML by
        // its own format so both panes render (and diff) through the same
        // type stack (§5).
        const oursFormat = detectFormat(localRaw);
        const theirsFormat = detectFormat(remoteRaw);
        if (oursFormat === "plaintext" && theirsFormat === "plaintext") {
            return { kind: "text" as const, oursText: localRaw, theirsText: remoteRaw, oursFormat, theirsFormat };
        }
        try {
            return {
                kind: "html" as const, oursFormat, theirsFormat,
                ...diffRendered(toRenderableHtml(localRaw, oursFormat), toRenderableHtml(remoteRaw, theirsFormat)),
            };
        } catch {
            return { kind: "failed" as const, oursFormat, theirsFormat };
        }
    }, [fieldDiff, currentField]);

    const oursRaw = displayValue(fieldDiff?.local);
    const theirsRaw = displayValue(fieldDiff?.remote);
    const runCount = rendered?.kind === "html" ? rendered.runCount : null;

    const resolve = async (which: "ours" | "theirs") => {
        setApplying(which);
        setApplyFailed(null);
        const ok = await (which === "theirs" ? onAccept() : onKeepOurs());
        setApplying(null);
        if (ok) onClose();
        else setApplyFailed(which);
    };

    const busy = applying !== null;
    const theirsLabel = mode === "conflict" ? `Take ${vendor.label} version` : `Update from ${vendor.label}`;

    return (
        <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent
                className="flex max-h-[calc(100vh-96px)] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[min(1240px,calc(100vw-64px))]"
                onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
            >
                {/* Header (§2) */}
                <DialogHeader className="space-y-1.5 border-b border-slate-200 bg-slate-50 px-6 pb-3 pt-5 text-left">
                    <div className="flex items-start justify-between gap-4 pr-8">
                        <DialogTitle className="text-[19px] font-semibold tracking-[-0.01em] text-slate-900">
                            {storyTitle || diff?.external_key || "Review changes"}
                            {diff?.external_key && (
                                <span className={cn(MONO, "ml-2 text-xs font-normal text-slate-400")}>{diff.external_key}</span>
                            )}
                        </DialogTitle>
                        {/* Rendered / Source segmented toggle */}
                        <div className="flex shrink-0 rounded-md bg-slate-200/60 p-0.5" role="group" aria-label="View mode">
                            {(["rendered", "source"] as const).map(v => (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => setView(v)}
                                    className={cn(
                                        "rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors",
                                        view === v ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700",
                                    )}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                    <DialogDescription className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1 rounded-full border border-[#bfdbfe] bg-[#eff6ff] px-2 py-0.5 text-[11px] font-medium text-[#1d4ed8]">
                            <vendor.Icon width={12} height={12} />
                            {`${vendor.label}${diff?.external_key ? ` · ${diff.external_key}` : ""}`}
                        </span>
                        <span>
                            {mode === "conflict"
                                ? "Both versions changed since the last sync."
                                : `Updated in ${vendor.label}.`}
                        </span>
                        {diff?.remote_updated_at && (
                            <span>{`Their edit ${new Date(diff.remote_updated_at).toLocaleString()}`}</span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {/* Field tab bar (§3) */}
                {!loading && diff && fields.length > 0 && (
                    <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-6">
                        {fields.map(f => (
                            <button
                                key={f.key}
                                type="button"
                                ref={f.key === currentKey ? activeTabRef : undefined}
                                onClick={() => setActiveField(f.key)}
                                className={cn(
                                    "flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-[13px]",
                                    f.key === currentKey
                                        ? "border-indigo-600 font-medium text-indigo-700"
                                        : "border-transparent text-slate-500 hover:text-slate-700",
                                    !f.differs && f.key !== currentKey && "text-slate-400",
                                )}
                            >
                                {f.label}
                                {f.differs && <span aria-label="differs" className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
                            </button>
                        ))}
                        <span className="ml-auto text-xs text-slate-400">
                            {`${differingCount} of ${fields.length} fields differ`}
                        </span>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
                    {loading && (
                        <>
                            {[0, 1].map(i => (
                                <div key={i} className="space-y-2 rounded-[11px] border border-slate-200 p-5">
                                    <Skeleton className="h-3 w-2/3 bg-slate-200" />
                                    <Skeleton className="h-3 w-full bg-slate-200" />
                                    <Skeleton className="h-3 w-4/5 bg-slate-200" />
                                </div>
                            ))}
                        </>
                    )}
                    {!loading && loadError && (
                        <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            {loadError}
                        </div>
                    )}
                    {!loading && diff && fields.length > 0 && differingCount === 0 && (
                        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                            Both versions are identical — nothing to review.
                        </p>
                    )}
                    {!loading && diff && currentField && (
                        <>
                            <Pane
                                side="ours"
                                vendor={vendor}
                                html={rendered?.kind === "html" ? rendered.oursHtml : undefined}
                                text={rendered?.kind === "text" ? rendered.oursText : undefined}
                                raw={oursRaw}
                                format={rendered?.oursFormat ?? "plaintext"}
                                view={view}
                                renderFailed={rendered?.kind === "failed"}
                                editedNote="Your work product"
                            />
                            {runCount !== null && view === "rendered" && (
                                <div className="flex items-center gap-3" aria-hidden={runCount === 0}>
                                    <span className="h-px flex-1 bg-slate-200" />
                                    <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400">
                                        {`${runCount} difference${runCount === 1 ? "" : "s"}`}
                                    </span>
                                    <span className="h-px flex-1 bg-slate-200" />
                                </div>
                            )}
                            <Pane
                                side="theirs"
                                vendor={vendor}
                                html={rendered?.kind === "html" ? rendered.theirsHtml : undefined}
                                text={rendered?.kind === "text" ? rendered.theirsText : undefined}
                                raw={theirsRaw}
                                format={rendered?.theirsFormat ?? "plaintext"}
                                view={view}
                                renderFailed={rendered?.kind === "failed"}
                                externalKey={diff.external_key}
                                externalUrl={diff.external_url}
                                editedNote={diff.remote_updated_at ? `Edited ${new Date(diff.remote_updated_at).toLocaleString()}` : ""}
                            />
                        </>
                    )}
                </div>

                {/* Apply failed (§8.6) */}
                {applyFailed && (
                    <div className="flex items-center justify-between gap-2 border-t border-rose-200 bg-rose-50 px-6 py-2 text-xs text-rose-800">
                        <span className="flex items-center gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            Couldn't apply that version — nothing was changed.
                        </span>
                        <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700 hover:bg-rose-100"
                                onClick={() => void resolve(applyFailed)}>
                            Retry
                        </Button>
                    </div>
                )}

                {/* Footer (§7) */}
                <div className="flex items-center gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
                    <p className="mr-auto text-xs text-slate-500">
                        {mode === "conflict"
                            ? "Whichever version you keep is written to both systems, so they match after this review."
                            : `Applying updates your work product from ${vendor.label}; keeping yours pushes it back to ${vendor.label}.`}
                    </p>
                    <Button
                        variant="outline"
                        disabled={busy || loading || !!loadError}
                        onClick={() => void resolve("ours")}
                        className="h-[38px] border-slate-300 bg-white"
                    >
                        {applying === "ours" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                        {applying === "ours" ? "Applying" : "Keep your version"}
                    </Button>
                    <Button
                        disabled={busy || loading || !!loadError}
                        onClick={() => void resolve("theirs")}
                        className="h-[38px] bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        {applying === "theirs" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                        {applying === "theirs" ? "Applying" : theirsLabel}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
